import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { AuthService } from '@interacta-shared/data-access-auth';
import {
    AvatarComponent,
    ButtonComponent,
    DialogComponent,
    KeyValueFieldDirective,
    LoaderComponent,
    SeparatorComponent,
    ToggleButtonComponent,
} from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';
import { isDefined } from '@interacta-shared/util';
import { IUserProfileField } from '@interacta-shared/util-caronte';
import { TranslatePipe } from '@ngx-translate/core';
import { UserProfileDialogService } from '../../services/user-profile-dialog.service';

@Component({
    selector: 'interacta-user-profile-dialog',
    templateUrl: './user-profile-dialog.component.html',
    imports: [
        AvatarComponent,
        LoaderComponent,
        DialogComponent,
        IconComponent,
        ButtonComponent,
        ToggleButtonComponent,
        SeparatorComponent,
        KeyValueFieldDirective,
        TranslatePipe,
        NgClass,
        RouterLink,
        MatTooltip,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileDialogComponent {
    private readonly userProfileDialogService = inject(
        UserProfileDialogService,
    );
    private readonly authService = inject(AuthService);

    readonly labelRoot = 'SHARED.USER_PROFILE.';

    data = computed(() => this.userProfileDialogService.data());
    user = computed(() => this.data()?.user);
    userIsCurrentUser = computed(
        () => this.user()?.id === this.authService.currentUserData()?.id,
    );
    canViewProfile = computed(
        () =>
            !this.user()?.privateProfile &&
            this.authService.currentUserData()?.userSettings?.viewUserProfiles,
    );

    isAdmin = computed(() => this.userProfileDialogService.showAdminInfo());
    profileLink = computed(() =>
        this.userProfileDialogService.navigate(this.user()?.id),
    );

    viewContent = computed(() => {
        const user = this.user();
        return (
            user &&
            !user.deleted &&
            (this.isAdmin() ||
                this.userIsCurrentUser() ||
                this.canViewProfile())
        );
    });

    items = computed<IUserProfileField[]>(() => {
        const user = this.user();
        const items: (IUserProfileField | undefined)[] = [
            {
                label: this.labelRoot + 'EMAIL',
                value: user?.contactEmail ?? '---',
                type: user?.contactEmail ? 'email' : 'string',
                testId: 'user-profile-field-email',
            },
            {
                label: this.labelRoot + 'TELEPHONE',
                value: user?.phone !== '' && user?.phone ? user.phone : '---',
                type: 'string',
                testId: 'user-profile-field-telephone',
            },
            user?.internalPhone
                ? {
                      label: this.labelRoot + 'EXTENSION_NUMBER',
                      value: user.internalPhone,
                      type: 'string',
                  }
                : undefined,
            user?.mobilePhone
                ? {
                      label: this.labelRoot + 'MOBILE',
                      value: user.mobilePhone,
                      type: 'string',
                  }
                : undefined,
            user?.place
                ? {
                      label: this.labelRoot + 'PLACE',
                      value: user.place,
                      type: 'string',
                      testId: 'user-profile-field-place',
                  }
                : undefined,
            user?.area?.name
                ? {
                      label: this.labelRoot + 'AREA',
                      value: user.area.name,
                      type: 'string',
                      testId: 'user-profile-field-area',
                  }
                : undefined,
            user?.businessUnit?.name
                ? {
                      label: this.labelRoot + 'BUSINESS_UNIT',
                      value: user.businessUnit.name,
                      type: 'string',
                      testId: 'user-profile-field-business-unit',
                  }
                : undefined,
        ];
        return items.filter(isDefined);
    });

    close(): void {
        this.userProfileDialogService.closeDialog();
    }

    toggleFollow(): void {
        this.userProfileDialogService.toggleFollow();
    }
}
